import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../context/AuthContext';

const API_URL = 'https://iia-backend-production.up.railway.app';

const AdminPanel = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();

  // State
  const [courses, setCourses] = useState([]);
  const [activeTab, setActiveTab] = useState('paths');
  const [students, setStudents] = useState([]);
  const [paths, setPaths] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [showCreateStudent, setShowCreateStudent] = useState(false);
  const [studentForm, setStudentForm] = useState({
    first_name: '', last_name: '', email: '', password: '', role_id: 4
  });
  const [editForm, setEditForm] = useState({ role_id: 4, status: 'active' });
  const [newPassword, setNewPassword] = useState('');
  const [enrollPathId, setEnrollPathId] = useState('');
  const [studentMessage, setStudentMessage] = useState('');
  const [loading, setLoading] = useState(false);

  // Form states
  const [newPath, setNewPath] = useState({ 
    title: '', 
    description: '', 
    thumbnail_url: '' 
  });
  const [newCourse, setNewCourse] = useState({ 
    title: '', 
    description: '', 
    thumbnail_url: '' 
  });

  const [message, setMessage] = useState('');

  useEffect(() => {
    // Redirect if not admin
    if (user && user.role_id !== 1) {
      navigate('/campus');
    }
    fetchData();
  }, [user, navigate]);
  useEffect(() => {
    if (activeTab === 'students') fetchStudents();
  }, [activeTab]);

  const fetchData = async () => {
    try {
      const [pathsRes, coursesRes] = await Promise.all([
        axios.get(`${API_URL}/api/instructional/paths/all`),
        axios.get(`${API_URL}/api/instructional/courses/all`)
      ]);
      setPaths(pathsRes.data.paths);
      setCourses(coursesRes.data.courses);
    } catch (err) {
      console.error('Fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchStudents = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/students`);
      setStudents(res.data.students);
    } catch (err) {
      console.error('Fetch students error:', err);
    }
  };

  const handleCreateStudent = async () => {
    try {
      await axios.post(`${API_URL}/api/students`, studentForm);
      setStudentMessage('Student created successfully');
      setShowCreateStudent(false);
      setStudentForm({ first_name: '', last_name: '', email: '', password: '', role_id: 4 });
      fetchStudents();
    } catch (err) {
      setStudentMessage(err.response?.data?.message || 'Error creating student');
    }
  };

  const handleUpdateStudent = async () => {
    try {
      await axios.patch(`${API_URL}/api/students/${selectedStudent.id}`, editForm);
      setStudentMessage('Student updated');
      fetchStudents();
      setSelectedStudent(null);
    } catch (err) {
      setStudentMessage('Error updating student');
    }
  };

  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 8) {
      setStudentMessage('Password must be at least 8 characters');
      return;
    }
    try {
      await axios.patch(
        `${API_URL}/api/students/${selectedStudent.id}/reset-password`,
        { password: newPassword }
      );
      setStudentMessage('Password reset successfully');
      setNewPassword('');
    } catch (err) {
      setStudentMessage('Error resetting password');
    }
  };

  const handleDeleteStudent = async (studentId) => {
    if (!window.confirm('Delete this student? This cannot be undone.')) return;
    try {
      await axios.delete(`${API_URL}/api/students/${studentId}`);
      setStudentMessage('Student deleted');
      setSelectedStudent(null);
      fetchStudents();
    } catch (err) {
      setStudentMessage('Error deleting student');
    }
  };

  const handleEnroll = async () => {
    if (!enrollPathId) return;
    try {
      await axios.post(`${API_URL}/api/students/${selectedStudent.id}/enroll`, {
        path_id: enrollPathId
      });
      setStudentMessage('Student enrolled in path');
      setEnrollPathId('');
      fetchStudents();
    } catch (err) {
      setStudentMessage('Error enrolling student');
    }
  };

  const handleUnenroll = async (pathTitle) => {
    const path = paths.find(p => p.title === pathTitle);
    if (!path) return;
    if (!window.confirm(`Remove from ${pathTitle}?`)) return;
    try {
      await axios.delete(
        `${API_URL}/api/students/${selectedStudent.id}/enroll/${path.id}`
      );
      setStudentMessage('Student unenrolled');
      fetchStudents();
    } catch (err) {
      setStudentMessage('Error unenrolling student');
    }
  };

  const handleCreatePath = async () => {
    try {
      await axios.post(`${API_URL}/api/instructional/paths`, newPath);
      setMessage('Path created successfully');
      setNewPath({ title: '', description: '', thumbnail_url: '' });
      fetchData();
    } catch (err) {
      setMessage('Error creating path');
    }
  };

  const handleCreateCourse = async () => {
    try {
      await axios.post(`${API_URL}/api/instructional/courses`, newCourse);
      setMessage('Course created successfully');
      setNewCourse({ title: '', description: '', thumbnail_url: '' });
      fetchData();
    } catch (err) {
      setMessage('Error creating course');
    }
  };

  const handlePublishPath = async (pathId, currentStatus) => {
    try {
      await axios.patch(
        `${API_URL}/api/instructional/paths/${pathId}/publish`,
        { is_published: !currentStatus }
      );
      fetchData();
    } catch (err) {
      setMessage('Error updating path');
    }
  };
  const handlePublishCourse = async (courseId, currentStatus) => {
  try {
    await axios.patch(
      `${API_URL}/api/instructional/courses/${courseId}/publish`,
      { is_published: !currentStatus }
    );
    fetchData();
  } catch (err) {
    setMessage('Error updating course');
  }
};

const handleDeleteCourse = async (courseId) => {
  if (!window.confirm('Delete this course? This cannot be undone.')) return;
  try {
    await axios.delete(`${API_URL}/api/instructional/courses/${courseId}`);
    setMessage('Course deleted');
    fetchData();
  } catch (err) {
    setMessage('Error deleting course');
  }
};

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <h1 style={styles.logo}>IIA</h1>
          <span style={styles.logoText}>Admin Panel</span>
        </div>
        <button 
          style={styles.returnBtn} 
          onClick={() => navigate('/campus')}
        >
          ← Return to Campus Square
        </button>
      </div>

      <div style={styles.main}>
        <h2 style={styles.pageTitle}>⚙️ Admin Panel</h2>

        {message && (
          <div style={styles.message}>{message}</div>
        )}

        {/* Tabs */}
        <div style={styles.tabs}>
          {['paths', 'courses', 'students'].map(tab => (
            <button
              key={tab}
              style={activeTab === tab ? styles.tabActive : styles.tab}
              onClick={() => setActiveTab(tab)}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>

        {/* PATHS TAB */}
        {activeTab === 'paths' && (
          <div style={styles.tabContent}>
            {/* Create Path Form */}
            <div style={styles.formCard}>
              <h3 style={styles.formTitle}>Create New Path</h3>
              <input
                style={styles.input}
                placeholder="Path title"
                value={newPath.title}
                onChange={e => setNewPath({...newPath, title: e.target.value})}
              />
              <textarea
                style={styles.textarea}
                placeholder="Path description"
                value={newPath.description}
                onChange={e => setNewPath({...newPath, description: e.target.value})}
              />
              <input
                style={styles.input}
                placeholder="Thumbnail URL (optional)"
                value={newPath.thumbnail_url}
                onChange={e => setNewPath({...newPath, thumbnail_url: e.target.value})}
              />
              <button style={styles.createBtn} onClick={handleCreatePath}>
                Create Path
              </button>
            </div>

            {/* Paths List */}
            <div style={styles.listSection}>
              <h3 style={styles.listTitle}>All Paths ({paths.length})</h3>
              {paths.length === 0 && (
                <p style={styles.emptyText}>No paths created yet</p>
              )}
              {paths.map(path => (
                <div key={path.id} style={styles.listCard}>
                  <div style={styles.listCardLeft}>
                    <h4 style={styles.listCardTitle}>{path.title}</h4>
                    <p style={styles.listCardDesc}>{path.description}</p>
                  </div>
                  <div style={styles.listCardRight}>
                    <span style={path.is_published ? styles.badgePublished : styles.badgeDraft}>
                      {path.is_published ? 'Published' : 'Draft'}
                    </span>
                    <button
                      style={styles.publishBtn}
                      onClick={() => handlePublishPath(path.id, path.is_published)}
                    >
                      {path.is_published ? 'Unpublish' : 'Publish'}
                    </button>
                    <button
                      style={styles.manageBtn}
                      onClick={() => navigate(`/admin/path/${path.id}`)}
                    >
                      Manage →
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* COURSES TAB */}
        {activeTab === 'courses' && (
          <div style={styles.tabContent}>
            {/* Create Course Form */}
            <div style={styles.formCard}>
              <h3 style={styles.formTitle}>Create New Course</h3>
              <input
                style={styles.input}
                placeholder="Course title"
                value={newCourse.title}
                onChange={e => setNewCourse({...newCourse, title: e.target.value})}
              />
              <textarea
                style={styles.textarea}
                placeholder="Course description"
                value={newCourse.description}
                onChange={e => setNewCourse({...newCourse, description: e.target.value})}
              />
              <input
                style={styles.input}
                placeholder="Thumbnail URL (optional)"
                value={newCourse.thumbnail_url}
                onChange={e => setNewCourse({...newCourse, thumbnail_url: e.target.value})}
              />
              <button style={styles.createBtn} onClick={handleCreateCourse}>
                Create Course
              </button>
            </div>

            {/* Courses List */}
            <div style={styles.listSection}>
              <h3 style={styles.listTitle}>All Courses ({courses.length})</h3>
              {courses.length === 0 && (
                <p style={styles.emptyText}>No courses created yet</p>
              )}
              {courses.map(course => (
                <div key={course.id} style={styles.listCard}>
                  <div style={styles.listCardLeft}>
                    <h4 style={styles.listCardTitle}>{course.title}</h4>
                    <p style={styles.listCardDesc}>{course.description}</p>
                  </div>
             <div style={styles.listCardRight}>
                    <span style={course.is_published ? styles.badgePublished : styles.badgeDraft}>
                      {course.is_published ? 'Published' : 'Draft'}
                    </span>
                    <button
                      style={styles.publishBtn}
                      onClick={() => handlePublishCourse(course.id, course.is_published)}
                    >
                      {course.is_published ? 'Unpublish' : 'Publish'}
                    </button>
                    <button
                      style={styles.manageBtn}
                      onClick={() => navigate(`/admin/course/${course.id}`)}
                    >
                      Manage →
                    </button>
                    <button
                      style={styles.deleteBtn}
                      onClick={() => handleDeleteCourse(course.id)}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* STUDENTS TAB */}
        {activeTab === 'students' && (
          <div style={styles.tabContent}>
            {studentMessage && (
              <div style={styles.message}>{studentMessage}</div>
            )}

            <div style={styles.studentsLayout}>
              {/* Students List */}
              <div style={styles.studentsList}>
                <div style={styles.studentsListHeader}>
                  <h3 style={styles.listTitle}>All Students ({students.length})</h3>
                  <button
                    style={styles.createBtn}
                    onClick={() => setShowCreateStudent(!showCreateStudent)}
                  >
                    + Add Student
                  </button>
                </div>

                {showCreateStudent && (
                  <div style={styles.formCard}>
                    <h3 style={styles.formTitle}>Create Student Account</h3>
                    <div style={styles.formRow}>
                      <input
                        style={styles.input}
                        placeholder="First name"
                        value={studentForm.first_name}
                        onChange={e => setStudentForm({...studentForm, first_name: e.target.value})}
                      />
                      <input
                        style={styles.input}
                        placeholder="Last name"
                        value={studentForm.last_name}
                        onChange={e => setStudentForm({...studentForm, last_name: e.target.value})}
                      />
                    </div>
                    <input
                      style={styles.input}
                      placeholder="Email"
                      value={studentForm.email}
                      onChange={e => setStudentForm({...studentForm, email: e.target.value})}
                    />
                    <input
                      style={styles.input}
                      type="password"
                      placeholder="Password (min 8 characters)"
                      value={studentForm.password}
                      onChange={e => setStudentForm({...studentForm, password: e.target.value})}
                    />
                    <select
                      style={styles.select}
                      value={studentForm.role_id}
                      onChange={e => setStudentForm({...studentForm, role_id: parseInt(e.target.value)})}
                    >
                      <option value={4}>Student</option>
                      <option value={3}>Staff</option>
                      <option value={2}>Instructor</option>
                    </select>
                    <button style={styles.createBtn} onClick={handleCreateStudent}>
                      Create Account
                    </button>
                  </div>
                )}

                {students.length === 0 && (
                  <p style={styles.emptyText}>No students yet.</p>
                )}

                {students.map(student => (
                  <div
                    key={student.id}
                    style={selectedStudent?.id === student.id ?
                      styles.studentCardActive : styles.studentCard}
                    onClick={() => {
                      setSelectedStudent(student);
                      setEditForm({ role_id: student.role_id, status: student.status });
                      setStudentMessage('');
                    }}
                  >
                    <div style={styles.studentCardLeft}>
                      <div style={styles.studentAvatar}>
                        {student.first_name?.[0]}{student.last_name?.[0]}
                      </div>
                      <div>
                        <p style={styles.studentName}>
                          {student.first_name} {student.last_name}
                        </p>
                        <p style={styles.studentEmail}>{student.email}</p>
                      </div>
                    </div>
                    <div style={styles.studentCardRight}>
                      <span style={{
                        ...styles.statusBadge,
                        backgroundColor: student.status === 'active' ? '#1E844920' : '#ff000015',
                        color: student.status === 'active' ? '#1E8449' : '#ff6666',
                      }}>
                        {student.status}
                      </span>
                      <span style={styles.roleBadge}>{student.role_name}</span>
                    </div>
                  </div>
                ))}
              </div>

              {/* Student Detail */}
              <div style={styles.studentDetail}>
                {!selectedStudent && (
                  <div style={styles.noSelection}>
                    <p style={styles.noSelectionText}>Select a student to manage</p>
                  </div>
                )}

                {selectedStudent && (
                  <>
                    <div style={styles.detailHeader}>
                      <h3 style={styles.detailName}>
                        {selectedStudent.first_name} {selectedStudent.last_name}
                      </h3>
                      <p style={styles.detailEmail}>{selectedStudent.email}</p>
                      <p style={styles.detailMeta}>
                        Joined {new Date(selectedStudent.created_at).toLocaleDateString()}
                      </p>
                    </div>

                    {/* Edit Role & Status */}
                    <div style={styles.detailSection}>
                      <h4 style={styles.detailSectionTitle}>Role & Status</h4>
                      <div style={styles.formRow}>
                        <select
                          style={styles.select}
                          value={editForm.role_id}
                          onChange={e => setEditForm({
                            ...editForm, role_id: parseInt(e.target.value)
                          })}
                        >
                          <option value={4}>Student</option>
                          <option value={3}>Staff</option>
                          <option value={2}>Instructor</option>
                        </select>
                        <select
                          style={styles.select}
                          value={editForm.status}
                          onChange={e => setEditForm({
                            ...editForm, status: e.target.value
                          })}
                        >
                          <option value="active">Active</option>
                          <option value="pending">Pending</option>
                          <option value="rejected">Rejected</option>
                        </select>
                      </div>
                      <button style={styles.saveEditBtn} onClick={handleUpdateStudent}>
                        Save Changes
                      </button>
                    </div>

                    {/* Enrolled Paths */}
                    <div style={styles.detailSection}>
                      <h4 style={styles.detailSectionTitle}>Enrolled Paths</h4>
                      {(!selectedStudent.enrolled_paths || 
                        selectedStudent.enrolled_paths.length === 0) && (
                        <p style={styles.emptyText}>Not enrolled in any paths.</p>
                      )}
                      {selectedStudent.enrolled_paths?.map(pathTitle => (
                        <div key={pathTitle} style={styles.enrolledPathRow}>
                          <span style={styles.enrolledPathName}>{pathTitle}</span>
                          <button
                            style={styles.unenrollBtn}
                            onClick={() => handleUnenroll(pathTitle)}
                          >
                            Remove
                          </button>
                        </div>
                      ))}
                      <div style={styles.enrollRow}>
                        <select
                          style={styles.select}
                          value={enrollPathId}
                          onChange={e => setEnrollPathId(e.target.value)}
                        >
                          <option value="">Select a path to enroll...</option>
                          {paths.map(path => (
                            <option key={path.id} value={path.id}>
                              {path.title}
                            </option>
                          ))}
                        </select>
                        <button style={styles.enrollBtn} onClick={handleEnroll}>
                          Enroll
                        </button>
                      </div>
                    </div>

                    {/* Reset Password */}
                    <div style={styles.detailSection}>
                      <h4 style={styles.detailSectionTitle}>Reset Password</h4>
                      <div style={styles.formRow}>
                        <input
                          style={styles.input}
                          type="password"
                          placeholder="New password (min 8 characters)"
                          value={newPassword}
                          onChange={e => setNewPassword(e.target.value)}
                        />
                        <button style={styles.saveEditBtn} onClick={handleResetPassword}>
                          Reset
                        </button>
                      </div>
                    </div>

                    {/* Delete */}
                    <div style={styles.detailSection}>
                      <button
                        style={styles.deleteStudentBtn}
                        onClick={() => handleDeleteStudent(selectedStudent.id)}
                      >
                        Delete Student Account
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const styles = {
  container: {
    minHeight: '100vh',
    backgroundColor: '#1A2744',
    color: '#ffffff',
  },
  header: {
    backgroundColor: '#0f1a33',
    borderBottom: '1px solid #C49A28',
    padding: '16px 32px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  logo: {
    color: '#C49A28',
    fontSize: '24px',
    fontWeight: '800',
    margin: '0',
    letterSpacing: '2px',
  },
  logoText: {
    color: '#ffffff80',
    fontSize: '14px',
    letterSpacing: '1px',
  },
  returnBtn: {
    backgroundColor: 'transparent',
    border: '1px solid #C49A28',
    color: '#C49A28',
    padding: '8px 16px',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '13px',
  },
  main: {
    maxWidth: '1100px',
    margin: '0 auto',
    padding: '32px',
  },
  pageTitle: {
    color: '#C49A28',
    fontSize: '28px',
    fontWeight: '700',
    margin: '0 0 24px 0',
  },
  message: {
    backgroundColor: '#1E8449',
    color: '#ffffff',
    padding: '12px 16px',
    borderRadius: '6px',
    marginBottom: '24px',
    fontSize: '14px',
  },
  tabs: {
    display: 'flex',
    gap: '8px',
    marginBottom: '24px',
    borderBottom: '1px solid #ffffff15',
    paddingBottom: '0',
  },
  tab: {
    backgroundColor: 'transparent',
    border: 'none',
    color: '#ffffff60',
    padding: '12px 24px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '600',
    borderBottom: '2px solid transparent',
  },
  tabActive: {
    backgroundColor: 'transparent',
    border: 'none',
    color: '#C49A28',
    padding: '12px 24px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '600',
    borderBottom: '2px solid #C49A28',
  },
  tabContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
  },
  formCard: {
    backgroundColor: '#0f1a33',
    border: '1px solid #ffffff15',
    borderRadius: '10px',
    padding: '24px',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  formTitle: {
    color: '#ffffff',
    fontSize: '16px',
    fontWeight: '600',
    margin: '0 0 8px 0',
  },
  input: {
    backgroundColor: '#1A2744',
    border: '1px solid #ffffff20',
    borderRadius: '6px',
    padding: '12px 16px',
    color: '#ffffff',
    fontSize: '14px',
    outline: 'none',
  },
  textarea: {
    backgroundColor: '#1A2744',
    border: '1px solid #ffffff20',
    borderRadius: '6px',
    padding: '12px 16px',
    color: '#ffffff',
    fontSize: '14px',
    outline: 'none',
    minHeight: '80px',
    resize: 'vertical',
  },
  createBtn: {
    backgroundColor: '#C49A28',
    color: '#1A2744',
    border: 'none',
    borderRadius: '6px',
    padding: '12px 24px',
    fontSize: '14px',
    fontWeight: '700',
    cursor: 'pointer',
    alignSelf: 'flex-start',
  },
  listSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  listTitle: {
    color: '#ffffff',
    fontSize: '16px',
    fontWeight: '600',
    margin: '0',
  },
  emptyText: {
    color: '#ffffff40',
    fontSize: '14px',
  },
  listCard: {
    backgroundColor: '#0f1a33',
    border: '1px solid #ffffff15',
    borderRadius: '10px',
    padding: '20px 24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '16px',
  },
  listCardLeft: {
    flex: 1,
  },
  listCardTitle: {
    color: '#ffffff',
    fontSize: '16px',
    fontWeight: '600',
    margin: '0 0 4px 0',
  },
  listCardDesc: {
    color: '#ffffff60',
    fontSize: '13px',
    margin: '0',
  },
  listCardRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  badgePublished: {
    backgroundColor: '#1E844920',
    color: '#1E8449',
    padding: '4px 10px',
    borderRadius: '20px',
    fontSize: '11px',
    fontWeight: '600',
  },
  badgeDraft: {
    backgroundColor: '#ffffff10',
    color: '#ffffff60',
    padding: '4px 10px',
    borderRadius: '20px',
    fontSize: '11px',
    fontWeight: '600',
  },
  publishBtn: {
    backgroundColor: 'transparent',
    border: '1px solid #ffffff30',
    color: '#ffffff80',
    padding: '6px 12px',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '12px',
  },
  manageBtn: {
    backgroundColor: '#1A6B8A',
    color: '#ffffff',
    border: 'none',
    padding: '6px 14px',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '12px',
    fontWeight: '600',
  },
    deleteBtn: {
    backgroundColor: 'transparent',
    border: '1px solid #ff000030',
    color: '#ff6666',
    padding: '6px 12px',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '12px',
  },
studentsLayout: { display: 'grid', gridTemplateColumns: '320px 1fr', gap: '0', minHeight: '500px' },
  studentsList: { backgroundColor: '#0f1a33', border: '1px solid #ffffff15', borderRadius: '10px 0 0 10px', overflow: 'hidden', display: 'flex', flexDirection: 'column' },
  studentsListHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid #ffffff10' },
  studentCard: { padding: '14px 20px', borderBottom: '1px solid #ffffff08', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  studentCardActive: { padding: '14px 20px', borderBottom: '1px solid #ffffff08', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', backgroundColor: '#1A2744' },
  studentCardLeft: { display: 'flex', alignItems: 'center', gap: '10px' },
  studentAvatar: { width: '34px', height: '34px', borderRadius: '50%', backgroundColor: '#1A6B8A', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#ffffff', fontSize: '12px', fontWeight: '700', flexShrink: 0 },
  studentName: { color: '#ffffff', fontSize: '13px', fontWeight: '600', margin: '0' },
  studentEmail: { color: '#ffffff50', fontSize: '11px', margin: '0' },
  studentCardRight: { display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px' },
  statusBadge: { fontSize: '10px', fontWeight: '700', padding: '2px 8px', borderRadius: '4px', textTransform: 'uppercase' },
  roleBadge: { color: '#ffffff40', fontSize: '10px' },
  studentDetail: { backgroundColor: '#0d1628', border: '1px solid #ffffff15', borderLeft: 'none', borderRadius: '0 10px 10px 0', padding: '28px', overflowY: 'auto' },
  noSelection: { display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' },
  noSelectionText: { color: '#ffffff30', fontSize: '14px' },
  detailHeader: { marginBottom: '24px', paddingBottom: '20px', borderBottom: '1px solid #ffffff10' },
  detailName: { color: '#ffffff', fontSize: '20px', fontWeight: '700', margin: '0 0 4px 0' },
  detailEmail: { color: '#C49A28', fontSize: '13px', margin: '0 0 4px 0' },
  detailMeta: { color: '#ffffff40', fontSize: '12px', margin: '0' },
  detailSection: { marginBottom: '24px', paddingBottom: '24px', borderBottom: '1px solid #ffffff08' },
  detailSectionTitle: { color: '#ffffff80', fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '1px', margin: '0 0 12px 0' },
  formRow: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '10px' },
  select: { backgroundColor: '#1A2744', border: '1px solid #ffffff20', borderRadius: '6px', padding: '10px 14px', color: '#ffffff', fontSize: '13px', outline: 'none', width: '100%' },
  saveEditBtn: { backgroundColor: '#1A6B8A', color: '#ffffff', border: 'none', borderRadius: '6px', padding: '8px 20px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' },
  enrolledPathRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', backgroundColor: '#1A2744', borderRadius: '6px', padding: '8px 12px', marginBottom: '6px' },
  enrolledPathName: { color: '#ffffff', fontSize: '13px' },
  unenrollBtn: { backgroundColor: 'transparent', border: '1px solid #ff000030', color: '#ff6666', borderRadius: '4px', padding: '3px 10px', fontSize: '11px', cursor: 'pointer' },
  enrollRow: { display: 'flex', gap: '8px', marginTop: '10px' },
  enrollBtn: { backgroundColor: '#C49A28', color: '#1A2744', border: 'none', borderRadius: '6px', padding: '8px 16px', fontSize: '13px', fontWeight: '700', cursor: 'pointer', whiteSpace: 'nowrap' },
  deleteStudentBtn: { backgroundColor: 'transparent', border: '1px solid #ff000040', color: '#ff6666', borderRadius: '6px', padding: '10px 20px', fontSize: '13px', cursor: 'pointer' },};

export default AdminPanel;